import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';

import { PATHS } from './paths';
import { SectionKeys } from './settings/settings';

const Activities = lazy(() => import('@app/pages/Activities'));
const Automations = lazy(() => import('@app/pages/Automations'));
const AutomationCanvas = lazy(() => import('@app/pages/Automations/Canvas'));
const AutomationReport = lazy(() => import('@app/pages/Automations/Report'));
const ClockInOutReport = lazy(() => import('@app/pages/Reports/ClockInOut'));
const Dashboard = lazy(() => import('@app/pages/Dashboard'));
const DashboardIndex = lazy(() => import('@app/pages/DashboardIndex'));
const DealWorkflow = lazy(() => import('@app/pages/DealWorkflow'));
const Error404 = lazy(() => import('@app/pages/404'));
const ForgotPassword = lazy(() => import('@app/pages/ForgotPassword'));
const Guide = lazy(() => import('@app/pages/Guide'));
const LeadDetail = lazy(() => import('@app/pages/Leads/LeadDetail'));
const Leads = lazy(() => import('@app/pages/Leads'));
const ListDetail = lazy(() => import('@app/pages/Lists/ListDetail'));
const Lists = lazy(() => import('@app/pages/Lists'));
const LoginPage = lazy(() => import('@app/pages/Login'));
const LostPage = lazy(() => import('@app/pages/Lost'));
const Logout = lazy(() => import('@app/pages/LogoutPage'));
const ManageActivities = lazy(() => import('@app/pages/Activities/Manage'));
const ManageAutomations = lazy(() => import('@app/pages/Automations/Manage'));
const ManageLeads = lazy(() => import('@app/pages/Leads/Manage'));
const ManageLists = lazy(() => import('@app/pages/Lists/Manage'));
const ManageTasks = lazy(() => import('@app/pages/Tasks/Manage'));
const Reports = lazy(() => import('@app/pages/Reports'));
const Requests = lazy(() => import('@app/pages/Requests'));
const ScheduledAiCalls = lazy(() => import('@app/pages/ScheduledAiCalls'));
const ResetPassword = lazy(() => import('@app/pages/ResetPassword'));
const Settings = lazy(() => import('@app/pages/Settings'));
const SmartViews = lazy(() => import('@app/pages/SmartViews'));
const Tasks = lazy(() => import('@app/pages/Tasks'));
const TenantRoot = lazy(() => import('@app/pages/TenantRoot'));
const AdminBecomes = lazy(() => import('@app/pages/admin/becomes/email'));
const Filters = lazy(() => import('@app/pages/Filters'));

export const appRoutesConfig = (): RouteObject[] => [
  {
    path: PATHS.adminBecomes(),
    element: <AdminBecomes />,
    errorElement: <Error404 />,
  },
  {
    element: <TenantRoot />,
    errorElement: <Error404 />,
    children: [
      {
        path: PATHS.login,
        element: <LoginPage />,
        errorElement: <Error404 />,
      },
      {
        path: PATHS.logout,
        element: <Logout />,
        errorElement: <Error404 />,
      },
      {
        path: PATHS.forgotPassword,
        element: <ForgotPassword />,
        errorElement: <Error404 />,
      },
      {
        path: PATHS.resetPassword,
        element: <ResetPassword />,
        errorElement: <Error404 />,
      },
      {
        path: PATHS.dashboard,
        element: <Dashboard />,
        errorElement: <Error404 />,
        children: [
          {
            index: true,
            element: <DashboardIndex />,
          },
          {
            path: PATHS.leads,
            element: <Leads />,
            children: [
              {
                index: true,
                // Redirect traffic from /leads to /leads/manage
                element: <Navigate to={PATHS.manageLeads} replace />,
              },
              {
                path: PATHS.manageLeads,
                element: <ManageLeads />,
              },
              {
                path: PATHS.leadDetail(),
                element: <LeadDetail />,
              },
            ],
          },
          {
            path: PATHS.activities,
            element: <Activities />,
            children: [
              {
                index: true,
                // Redirect traffic from /activities to /activities/manage
                element: <Navigate to={PATHS.manageActivities} replace />,
              },
              {
                path: PATHS.manageActivities,
                element: <ManageActivities />,
              },
            ],
          },
          {
            path: PATHS.tasks,
            element: <Tasks />,
            children: [
              {
                index: true,
                // Redirect traffic from /tasks to /tasks/manage
                element: <Navigate to={PATHS.manageTasks} replace />,
              },
              {
                path: PATHS.manageTasks,
                element: <ManageTasks />,
              },
            ],
          },
          {
            path: PATHS.guide,
            element: <Guide />,
          },
          {
            path: PATHS.automations,
            element: <Automations />,
            children: [
              {
                index: true,
                element: <Navigate to={PATHS.manageAutomations} replace />,
              },
              {
                path: PATHS.manageAutomations,
                element: <ManageAutomations />,
              },
              {
                path: PATHS.automationCanvas(),
                element: <AutomationCanvas />,
              },
              {
                path: PATHS.automationReport(),
                element: <AutomationReport />,
              },
              {
                path: PATHS.automationIndividualReport(),
                element: <AutomationReport />,
              },
            ],
          },
          {
            path: PATHS.lists,
            element: <Lists />,
            children: [
              {
                index: true,
                // Redirect traffic from /lists to /lists/manage
                element: <Navigate to={PATHS.manageLists} replace />,
              },
              {
                path: PATHS.manageLists,
                element: <ManageLists />,
              },
              {
                path: PATHS.listDetail(),
                element: <ListDetail />,
              },
            ],
          },
          {
            path: PATHS.dealWorkflow,
            element: <DealWorkflow />,
          },
          {
            path: PATHS.smartViews,
            element: <SmartViews />,
          },
          {
            path: PATHS.scheduledAiCalls,
            element: <ScheduledAiCalls />,
          },
          {
            path: PATHS.reports,
            element: <Reports />,
            children: [
              {
                index: true,
                element: <Navigate to={PATHS.clockInReport} replace />,
              },
              {
                path: PATHS.clockInReport,
                element: <ClockInOutReport />,
              },
            ],
          },
          {
            path: PATHS.settings,
            children: [
              {
                index: true,
                element: <Navigate to={`${PATHS.settings}/${SectionKeys.my_profile}`} replace />,
              },
              {
                path: PATHS.settingDetail(),
                element: <Settings />,
              },
            ],
          },
          {
            path: PATHS.requests,
            children: [
              {
                index: true,
                element: <Navigate to={PATHS.myRequests} replace />,
              },
              {
                path: PATHS.myRequests,
                element: <Requests />,
              },
              {
                path: PATHS.allRequests,
                element: <Requests />,
              },
            ],
          },
          {
            path: PATHS.filters,
            children: [
              {
                index: true,
                element: <Navigate to={PATHS.manageLeads} replace />,
              },
              {
                path: PATHS.filterWithSlug(),
                element: <Filters />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: PATHS.lost,
    element: <LostPage />,
  },
  {
    path: PATHS.error404,
    element: <Error404 />,
  },
  {
    path: '*',
    element: <Error404 />,
  },
];
